import { formatDateLocal } from 'src/utils/TimeZoneDateFormat'

// Data formatters for table cells

// Currency symbols map
export const currencySigns: Record<string, string> = {
  USD: '$', // United States Dollar
  INR: 'Γé╣', // Indian Rupee
  AUD: 'A$', // Australian Dollar
  AU: 'A$', // Australia (same as AUD)
  GBP: '┬ú', // British Pound Sterling
  EUR: 'Γé¼', // Euro
  NZD: 'NZ$', // New Zealand Dollar
  CAD: '$', // Canadian Dollar
  CHF: 'CHF', // Swiss Franc
  SEK: 'kr', // Swedish Krona
  KRW: 'Γé⌐', // South Korean Won
  JPY: '┬Ñ', // Japanese Yen
  HUF: 'Ft', // Hungarian Forint
  DKK: 'kr', // Danish Krone
  KWD: '╪».┘â', // Kuwaiti Dinar
  NPR: 'Γé¿', // Nepalese Rupee
  ILS: 'Γé¬', // Israeli New Shekel
  EGP: '╪¼.┘à', // Egyptian Pound
  PLN: 'z┼é', // Polish Zloty
  MXN: '$', // Mexican Peso
  AED: '╪».╪Ñ', // UAE Dirham
  SAR: '╪▒.╪│', // Saudi Riyal
  TRY: 'Γé║', // Turkish Lira
  MYR: 'RM', // Malaysian Ringgit
  HKD: '$', // Hong Kong Dollar
  CZK: 'K─ì', // Czech Koruna
  RON: 'lei', // Romanian Leu
  SGD: '$', // Singapore Dollar
  ZAR: 'R', // South African Rand
  MAD: '╪».┘à.', // Moroccan Dirham
  BGN: '╨╗╨▓', // Bulgarian Lev
  UGX: 'USh', // Ugandan Shilling
}

export const formatPrice = (value: any, currency: string = 'USD'): string => {
  if (value === null || value === undefined || value === '') return '-'
  
  // Handle if value is an object with price structure {type, value, currency_iso}
  if (typeof value === 'object' && value !== null) {
    if ('value' in value) {
      const priceValue = value.value
      const currencyCode = value.currency_iso || currency
      
      if (priceValue === null || priceValue === undefined) return '-'
      
      const num = typeof priceValue === 'string' ? parseFloat(priceValue) : priceValue
      if (isNaN(num)) return '-'
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num)
    }
    return '-'
  }
  
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

export const formatNumber = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'
  
  return new Intl.NumberFormat('en-US').format(num)
}

export const formatDate = (value: any, includeTime: boolean = true): string => {
  if (!value) return '-'
  try {
    return formatDateLocal(value)
  } catch {
    const date = new Date(value)
    if (isNaN(date.getTime())) return '-'
    if (includeTime) {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    }
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date)
  }
}

export const formatStatus = (value: any): string => {
  if (!value) return '-'
  return String(value).replace(/_/g, ' ').toUpperCase()
}

export const formatBoolean = (value: any): string => {
  if (value === null || value === undefined) return '-'
  return value ? 'Yes' : 'No'
}

export const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text) return '-'
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export const formatCellValue = (value: any, type?: string, format?: string): string => {
  if (value === null || value === undefined || value === '') return '-'
  
  switch (type) {
    case 'number':
      return formatNumber(value)
    case 'date':
      return formatDate(value)
    case 'status':
      return formatStatus(value)
    default:
      return String(value)
  }
}

/**
 * Extract cell value from row data based on field name
 * Handles nested properties and special field mappings
 */
export const getCellValue = (row: any, field: string): any => {
  if (!row) return null
  
  switch (field) {
    case 'product_name':
      return row.platform_product?.name ?? row.name ?? row.product_name ?? null
    
    case 'sku':
      return row.sku ?? row.channel_sku ?? row.platform_product?.sku ?? null
    
    case 'api_id':
      return row.api_id ?? row.channel_api_id ?? row.platform_product?.api_id ?? null
    
    case 'quantity':
      return row.quantity ?? row.inventory_level ?? row.platform_product?.quantity ?? row.platform_product?.inventory_level ?? null
    
    case 'price':
      return row.price?.value ?? row.price ?? row.platform_product?.price?.value ?? row.platform_product?.price ?? null

    case 'compare_price':
    case 'wholesale_price':
      return (
        row.compare_price?.value ??
        row.compare_price ??
        row.wholesale_price?.value ??
        row.wholesale_price ??
        row.platform_product?.compare_price?.value ??
        row.platform_product?.compare_price ??
        row.platform_product?.wholesale_price?.value ??
        row.platform_product?.wholesale_price ??
        null
      )
    
    case 'barcode':
      return row.barcode ?? row.platform_product?.barcode ?? null
    
    case 'title':
      return row.title ?? row.platform_product?.name ?? null
    
    case 'description':
      return row.description ?? row.platform_product?.description ?? null
    
    case 'tags':
      return row.tags ?? row.platform_product?.tags ?? null
    
    case 'product_type':
      return row.product_type ?? row.platform_product?.product_type ?? null
    
    case 'vendor':
      return row.vendor ?? row.platform_product?.vendor ?? null
    
    case 'created_at':
      return row.created_at ?? row.platform_product?.created_at ?? null
    
    case 'updated_at':
      return row.updated_at ?? row.platform_product?.updated_at ?? null
    
    case 'api_status':
      return row.platform_product?.api_status ?? row.api_status ?? null
    
    case 'sync_status':
      return row.sync_status ?? null
    
    case 'platform_product':
      return row.platform_product?.name ?? row.platform_product?.api_id ?? row.platform_product?.id ?? null

    case 'grouped_lite_actions':
      return null

    default:
      // Try direct property access first
      if (row[field] !== undefined) {
        return row[field]
      }
      
      // Try nested path (e.g., "platform_product.name")
      const parts = field.split('.')
      let current: any = row
      for (const part of parts) {
        if (current == null) break
        current = current[part]
      }
      
      return current ?? null
  }
}
